(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.MSProjectPlannerAccessUI = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const TEMPORARY = 'Planner access is temporarily unavailable. Please try again.';
  const DENIED = 'We could not unlock the Planner with that code. Please check your access code and try again, or request another access code so we can confirm your project location and provide you with a new code to unlock the Planner.';
  const INCOMPLETE = 'Please enter the complete 8-character access code.';

  function boundary(root, controller, present, restoreView) {
    let replaying = false;
    let pendingAction = null;
    let busy = false;
    let revision = 0;
    let changedBeforeBoundary = false;
    function resume() {
      revision++;
      busy = false;
      const action = pendingAction;
      pendingAction = null;
      if (!action || !controller.hasAuthorization()) return;
      replaying = true;
      try { action(); } finally { replaying = false; }
    }
    async function retry() {
      if (busy) return;
      busy = true;
      const current = revision;
      const result = await controller.authorize();
      busy = false;
      if (revision !== current) return;
      if (result.authorized) { present(null); resume(); }
      else { present(result.outcome); if (changedBeforeBoundary && restoreView) restoreView(); }
    }
    function intercept(event) {
      const target = event.target;
      if (replaying || !target?.closest || target.closest('[data-ms-access-panel]') || target.closest('[data-ms-access-tools]')) return;
      if (!target.closest('button, input, select, textarea, a')) return;
      // Native navigation through the page is public. Only actionable key presses enter the boundary.
      if (event.type === 'keydown' && (['Tab', 'Shift', 'Control', 'Alt', 'Meta', 'Escape'].includes(event.key) || event.ctrlKey || event.metaKey)) return;
      if (controller.hasAuthorization()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy || pendingAction) return;
      const value = target.value;
      const checked = target.checked;
      changedBeforeBoundary = ['input', 'change'].includes(event.type);
      pendingAction = () => {
        if (!target.isConnected) { if (restoreView) restoreView(); return; }
        target.focus();
        if (event.type === 'click') target.click();
        else if (['input', 'change'].includes(event.type)) {
          target.value = value;
          if (typeof checked === 'boolean') target.checked = checked;
          target.dispatchEvent(new Event(event.type, {bubbles: true}));
        } else if (event.type === 'keydown' && ['Enter', ' '].includes(event.key) && target.tagName === 'BUTTON') target.click();
        // Text entry prevented before mutation resumes at the focused field.
      };
      void retry();
    }
    for (const type of ['click', 'beforeinput', 'input', 'change', 'keydown']) root.addEventListener(type, intercept, true);
    function cancel() { revision++; busy = false; pendingAction = null; }
    return Object.freeze({retry, resume, cancel});
  }

  function mount(root, {restoreView, beforeRequestSubmit} = {}) {
    const panel = root.querySelector('[data-ms-access-panel]');
    const composition = window.MSProjectPlannerAccessComposition;
    let storage;
    try { storage = window.localStorage; } catch (_) { storage = null; }
    const access = window.MSProjectPlannerAccess;
    let controller;
    try {
      if (!access || !composition) throw new Error();
      const service = composition.service(access, window.fetch.bind(window));
      controller = access.create({environment: composition.environment, storage, service});
    } catch (_) {
      controller = {hasAuthorization: () => false, authorize: async () => ({authorized: false, outcome: 'TEMPORARY_ERROR'}),
        validate: async () => ({authorized: false, outcome: 'TEMPORARY_ERROR'})};
    }
    const find = selector => panel?.querySelector(selector);
    const message = find('[data-ms-access-message]');
    const entry = find('[data-ms-access-code-form]');
    const code = find('[data-ms-access-code]');
    const request = find('[data-ms-access-request]');
    const reveal = find('[data-ms-access-unlock]');
    const retry = find('[data-ms-access-retry]');
    const heading = find('[data-ms-access-heading]');
    const gateHeading = heading?.textContent;
    const handoff = window.MSProjectPlannerRequestHandoff;
    function requestState(open) {
      request.hidden = !open;
      for (const selector of ['intro', 'existing', 'request-open', 'unlock', 'close']) {
        const control = find('[data-ms-access-' + selector + ']');
        if (control) control.hidden = open;
      }
      if (heading) {
        heading.textContent = open ? 'REQUEST AN ACCESS CODE' : gateHeading;
      }
      panel.setAttribute('aria-labelledby', 'ms-project-planner-access-heading');
      if (open) { entry.hidden = true; retry.hidden = true; message.textContent = ''; }
    }
    const customerCode = window.MSProjectPlannerAccessCode;
    let retryValidation = false;
    function openEntry() { requestState(false); entry.hidden = false; reveal.hidden = true; code.focus(); }
    function clearEntry() {
      code.value = ''; code.removeAttribute('aria-invalid');
      entry.removeAttribute('data-ms-code-ready');
      entry.hidden = true; reveal.hidden = false; retry.hidden = true;
      retryValidation = false; message.textContent = '';
    }
    let returnFocus = null;
    let presentationRevision = 0;
    function present(outcome) {
      if (!panel) return;
      if (!outcome) { panel.hidden = true; return; }
      if (panel.hidden) returnFocus = document.activeElement;
      panel.hidden = false;
      message.textContent = outcome === 'TEMPORARY_ERROR' ? TEMPORARY : ['REVOKED', 'EXPIRED', 'INVALID'].includes(outcome) ? DENIED : '';
      retry.hidden = outcome !== 'TEMPORARY_ERROR';
      if (['REVOKED', 'EXPIRED', 'INVALID'].includes(outcome)) openEntry();
      else panel.focus();
    }
    const gate = boundary(root, controller, present, restoreView);
    // Shopify renders this error only after the native contact response;
    // it never grants authorization or triggers geographic screening.
    const requestResult = find('[data-ms-access-request-errors]');
    if (requestResult) {
      panel.hidden = false;
      requestState(true);
      requestResult.focus();
      requestResult.scrollIntoView({block: 'center'});
    }
    const requestForm = request?.querySelector('form');
    requestForm?.addEventListener('submit', event => {
      if (event.defaultPrevented) return;
      let saved = false;
      try { saved = beforeRequestSubmit?.() === true; } catch (_) { /* Ask before risking project loss. */ }
      if (!saved && !window.confirm('Your project could not be saved in this browser and may be lost when you send this request. Send request anyway?')) {
        event.preventDefault();
        return;
      }
      if (handoff && !handoff.prepare()) {
        event.preventDefault();
        message.textContent = 'Your request could not be prepared in this browser. Please enable session storage and try again.';
        return;
      }
      presentationRevision++;
      gate.cancel();
      // Allow Shopify's native POST. Its native Liquid result controls the confirmation redirect.
    });
    reveal?.addEventListener('click', openEntry);
    find('[data-ms-access-request-open]')?.addEventListener('click', () => {
      presentationRevision++;
      clearEntry();
      requestState(true);
      request.querySelector('input:not([type="hidden"])')?.focus();
    });
    find('[data-ms-access-request-back]')?.addEventListener('click', () => {
      presentationRevision++;
      clearEntry();
      openEntry();
    });
    retry?.addEventListener('click', () => {
      if (retry.hidden) return;
      if (retryValidation) void submitCode();
      else void gate.retry();
    });
    find('[data-ms-access-close]')?.addEventListener('click', () => {
      presentationRevision++;
      gate.cancel();
      clearEntry();
      requestState(false);
      panel.hidden = true;
      returnFocus?.focus();
    });
    code?.addEventListener('input', () => {
      const before = code.value;
      const caret = code.selectionStart;
      const formatted = customerCode.format(before);
      if (before !== formatted) {
        code.value = formatted;
        const position = customerCode.format(before.slice(0, caret)).length;
        code.setSelectionRange(position, position);
      }
      code.removeAttribute('aria-invalid');
      // Completeness is presentation only; authorization still requires submission.
      if (customerCode.normalize(code.value)) entry.setAttribute('data-ms-code-ready', 'true');
      else entry.removeAttribute('data-ms-code-ready');
      retry.hidden = true;
      retryValidation = false;
      message.textContent = '';
    });
    let validating = false;
    async function submitCode() {
      if (validating) return;
      const normalized = customerCode?.normalize(code.value);
      if (!normalized) {
        message.textContent = INCOMPLETE;
        retry.hidden = true;
        code.setAttribute('aria-invalid', 'true');
        code.focus();
        return;
      }
      code.value = normalized;
      validating = true;
      retry.hidden = true;
      const current = presentationRevision;
      const button = entry.querySelector('button');
      button.disabled = true;
      code.readOnly = true;
      button.textContent = 'CHECKING CODE…';
      entry.setAttribute('aria-busy', 'true');
      let result;
      try { result = await controller.validate(normalized); }
      catch (_) { result = {authorized: false, outcome: 'TEMPORARY_ERROR'}; }
      finally {
        validating = false;
        button.disabled = false;
        code.readOnly = false;
        button.textContent = 'UNLOCK PLANNER';
        entry.removeAttribute('aria-busy');
      }
      if (presentationRevision !== current) return;
      if (result.authorized) { clearEntry(); present(null); gate.resume(); }
      else {
        retryValidation = result.outcome === 'TEMPORARY_ERROR';
        present(result.outcome);
        code.setAttribute('aria-invalid', String(!retryValidation));
        code.focus();
        code.select();
      }
    }
    entry?.addEventListener('submit', event => { event.preventDefault(); return submitCode(); });
    composition?.attach?.({root, controller, storage, cancel: () => { presentationRevision++; gate.cancel(); clearEntry(); present(null); }});
    return controller;
  }
  return Object.freeze({boundary, mount, TEMPORARY, DENIED, INCOMPLETE});
});
